import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import BillingModalDeliveryNotesCrudComponent from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud.vue'
import BillingModalDeliveryNotesCrud from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud'
import BillingModalInvoicesCrudComponent from '@/modules/billing-module/components/modal/invoices/crud/billing-modal-invoices-crud.vue'
import BillingModalInvoicesCrud from '@/modules/billing-module/components/modal/invoices/crud/billing-modal-invoices-crud'
import BillingModalInvoicesAddToComponent from '@/modules/billing-module/components/modal/invoices/add-to/billing-modal-invoices-add-to.vue'
import BillingModalInvoicesAddTo from '@/modules/billing-module/components/modal/invoices/add-to/billing-modal-invoices-add-to'
import {
    BillableDeliveryNoteResponse,
    BillableDeliveryNoteStatus
} from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { StrictNullable } from '@/shared/types/app.types'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { billingCommonInvoiceService } from '@/api/services/billing/common-invoice/billing-common-invoice.service'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { ModalUtils } from '@/shared/util/modal-utils'
import { FileUtils } from '@/shared/util/file-utils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component({
    components: {
        BillingModalDeliveryNotesCrudComponent,
        BillingModalInvoicesCrudComponent,
        BillingModalInvoicesAddToComponent
    }
})
export default class BillingGridActionsDeliveryNotes extends Vue {
    @Ref() readonly deliveryNotesModal!: BillingModalDeliveryNotesCrud
    @Ref() readonly invoicesModal!: BillingModalInvoicesCrud
    @Ref() readonly invoicesAddToModal!: BillingModalInvoicesAddTo

    @Emit()
    refresh(): void {
        // emits an event
    }

    private static get _selectedRows(): BillableDeliveryNoteResponse[] {
        return BillingStoreHelper.masterSelectedRows as BillableDeliveryNoteResponse[]
    }
    private static get _selectedRow(): StrictNullable<BillableDeliveryNoteResponse> {
        return BillingGridActionsDeliveryNotes._selectedRows.length === 1
            ? BillingGridActionsDeliveryNotes._selectedRows[0]
            : null
    }

    get canUpdateDeliveryNote(): boolean {
        return BillingGridActionsDeliveryNotes._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_DELIVERY_NOTES)
    }
    get canDeleteDeliveryNote(): boolean {
        return (BillingGridActionsDeliveryNotes._selectedRow?.status.code === BillableDeliveryNoteStatus.GENERATED ||
            (BillingGridActionsDeliveryNotes._selectedRow?.status.code === BillableDeliveryNoteStatus.ISSUED && BillingGridActionsDeliveryNotes._selectedRow?.numberOfconcepts === 0)) &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_DELIVERY_NOTES)
    }
    get canCreateInvoice(): boolean {
        const hasAnySelectedRow =
            !!BillingGridActionsDeliveryNotes._selectedRows.length
        const sameAccountingUnit =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                selectedRow.conceptsAccountingUnit?.id === BillingGridActionsDeliveryNotes._selectedRows[0].conceptsAccountingUnit?.id)
        const sameIsForAutoInvoice =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                selectedRow.isForAutoInvoice === BillingGridActionsDeliveryNotes._selectedRows[0].isForAutoInvoice)
        const sameCustomerOrPatient =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                (!!selectedRow.customer && selectedRow.customer.id === BillingGridActionsDeliveryNotes._selectedRows[0].customer?.id) ||
                (!selectedRow.customer && selectedRow.patient?.id === BillingGridActionsDeliveryNotes._selectedRows[0].patient?.id))
        const allVerified =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                selectedRow.status.code === BillableDeliveryNoteStatus.VERIFIED)
        const hasNotInvoice =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow => !selectedRow.invoice)

        /// breakdowns
        const customerHasBreakdownByCaseType =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByCaseType &&
                selectedRow.caseType?.id !== BillingGridActionsDeliveryNotes._selectedRows[0].caseType?.id)
        const customerHasBreakdownByPatient =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByPatient &&
                selectedRow.patient?.id !== BillingGridActionsDeliveryNotes._selectedRows[0].patient?.id)
        const customerHasBreakdownByRegime =
            BillingGridActionsDeliveryNotes._selectedRows.every(selectedRow =>
                !!selectedRow.customer &&
                selectedRow.customer.breakdownDeliveryNotesByRegime &&
                selectedRow.regime?.id !== BillingGridActionsDeliveryNotes._selectedRows[0].regime?.id)

        return hasAnySelectedRow &&
            sameAccountingUnit &&
            sameIsForAutoInvoice &&
            sameCustomerOrPatient &&
            allVerified &&
            hasNotInvoice &&
            !customerHasBreakdownByCaseType &&
            !customerHasBreakdownByPatient &&
            !customerHasBreakdownByRegime &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICES)
    }
    get canAddToInvoice(): boolean {
        return this.canCreateInvoice
    }
    get canOpenDeliveryNoteReport(): boolean {
        return BillingGridActionsDeliveryNotes._selectedRows.length === 1 &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.MANAGE_INVOICES)
    }

    /// SERVICE CALLS ///

    private async _deleteDeliveryNote(billableDeliveryNoteId: number): Promise<boolean> {
        const response =
            await PromiseUtils.toCallSecure(
                billingCommonDeliveryNoteService.delete(
                    store.state.labId,
                    billableDeliveryNoteId))
        if (response !== null) {
            ToastUtils.showToastSuccess(this.$tc('accounting.deliveryNote.deleted'))
        }
        return response !== null
    }

    private _downloadDeliveryNoteReport(billableDeliveryNoteId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingCommonDeliveryNoteService.getReport(store.state.labId, billableDeliveryNoteId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// EVENT HANDLERS ///

    onUpdateDeliveryNote(): void {
        if (BillingGridActionsDeliveryNotes._selectedRow) {
            this.deliveryNotesModal.show({
                deliveryNote: BillingGridActionsDeliveryNotes._selectedRow
            })
        }
    }

    async onDeleteDeliveryNote(): Promise<void> {
        if (BillingGridActionsDeliveryNotes._selectedRow) {
            const { id, numberOfconcepts } = BillingGridActionsDeliveryNotes._selectedRow
            await BillingOperationWithConceptsGuard.run(
                numberOfconcepts,
                async () => {
                    if (await this._deleteDeliveryNote(id)) {
                        this.deliveryNotesModal.hide()
                        this.refresh()
                    }
                }
            )
        }
    }

    onCreateInvoice(): void {
        this.invoicesModal.show({
            deliveryNotes: BillingGridActionsDeliveryNotes._selectedRows
        })
    }

    async onAddToInvoice(): Promise<void> {
        await this.invoicesAddToModal.show({
            deliveryNotes: BillingGridActionsDeliveryNotes._selectedRows
        })
    }
    async onAddedToInvoice(invoiceId: number): Promise<void> {
        this.invoicesAddToModal.hide()
        this.refresh()

        const invoice =
            await PromiseUtils.toCallSecure(
                billingCommonInvoiceService.get(store.state.labId, invoiceId))
        if (invoice !== null) {
            this.invoicesModal.show({ invoice })
        }
    }

    async onOpenDeliveryNoteReport(): Promise<void> {
        if (BillingGridActionsDeliveryNotes._selectedRow) {
            const blob = await this._downloadDeliveryNoteReport(BillingGridActionsDeliveryNotes._selectedRow.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    ///

    getContextMenuItems(): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (this.canDeleteDeliveryNote) {
            menuItems.push({
                name: this.$tc('accounting.action.deleteDeliveryNote'),
                action: this.onDeleteDeliveryNote
            })
        }
        if (this.canUpdateDeliveryNote) {
            menuItems.push({
                name: this.$tc('accounting.action.updateDeliveryNote'),
                action: this.onUpdateDeliveryNote
            })
        }
        if (this.canCreateInvoice) {
            menuItems.push({
                name: this.$tc('accounting.action.generateInvoice'),
                action: this.onCreateInvoice
            })
        }
        if (this.canAddToInvoice) {
            menuItems.push({
                name: this.$tc('accounting.action.addToInvoice'),
                action: this.onAddToInvoice
            })
        }
        if (this.canOpenDeliveryNoteReport) {
            menuItems.push({
                name: this.$tc('global.action.openReport'),
                action: this.onOpenDeliveryNoteReport
            })
        }
        return menuItems
    }
}
